import { type AggregationLevel, type AggregatedRow, type GSRPCapacityReportModel, type ProviderWithFacilities } from './GSRPCapacityReport.types'

/**
 * Helper to accumulate capacity metrics into a target object
 */
function accumulateMetrics (
  target: {
    countPendingProcessing: number
    countEnrolled: number
    countWaitlisted: number
    totalActiveSeats: number
    totalAllocatedCapacity: number
  },
  source: GSRPCapacityReportModel
): void {
  target.countPendingProcessing += source.countPendingProcessing
  target.countEnrolled += source.countEnrolled
  target.countWaitlisted += source.countWaitlisted
  target.totalActiveSeats += source.totalActiveSeats
  target.totalAllocatedCapacity += source.totalAllocatedCapacity
}

/**
 * Create an empty aggregated row with base metrics
 */
function createEmptyAggregatedRow (row: GSRPCapacityReportModel, includeFacility: boolean): AggregatedRow {
  return {
    providerName: row.providerName,
    ...(includeFacility && {
      facilityName: row.facilityName,
      facilityAddress: row.facilityAddress
    }),
    countPendingProcessing: 0,
    countEnrolled: 0,
    countWaitlisted: 0,
    totalActiveSeats: 0,
    totalAllocatedCapacity: 0
  }
}

/**
 * Aggregate report data based on the selected aggregation level
 */
export function aggregateReportData (
  data: GSRPCapacityReportModel[],
  level: AggregationLevel
): AggregatedRow[] {
  const includeFacility = level === 'facility'
  const grouped = data.reduce<Record<string, AggregatedRow>>((acc, row) => {
    const key = includeFacility ? `${row.providerName}|${row.facilityName}` : row.providerName

    if (acc[key] === undefined) {
      acc[key] = createEmptyAggregatedRow(row, includeFacility)
    }

    accumulateMetrics(acc[key], row)
    return acc
  }, {})

  return Object.values(grouped)
}

/**
 * Calculate utilization percentage
 */
export function getUtilizationPercent (active: number, capacity: number): number {
  if (capacity === 0) return 0
  return Math.round((active / capacity) * 100)
}

/**
 * Get capacity status and color based on utilization
 */
export function getCapacityStatus (active: number, capacity: number): {
  status: 'available' | 'limited' | 'full'
  color: string
  backgroundColor: string
} {
  if (capacity === 0) {
    return {
      status: 'available',
      color: '#4DA4A4',
      backgroundColor: '#E7F3F2'
    }
  }

  const utilizationPercent = (active / capacity) * 100

  if (utilizationPercent >= 100) {
    return {
      status: 'full',
      color: '#E4536B',
      backgroundColor: '#E4AFBB'
    }
  } else if (utilizationPercent >= 80) {
    return {
      status: 'limited',
      color: '#F9D423',
      backgroundColor: '#FCF9EB'
    }
  }

  return {
    status: 'available',
    color: '#4DA4A4',
    backgroundColor: '#E7F3F2'
  }
}

/**
 * Group report data by provider with nested facilities
 */
export function groupByProviderWithFacilities (
  data: GSRPCapacityReportModel[]
): ProviderWithFacilities[] {
  const grouped = data.reduce<Record<string, ProviderWithFacilities>>((acc, row) => {
    const providerKey = row.providerName

    if (acc[providerKey] === undefined) {
      acc[providerKey] = {
        providerName: row.providerName,
        countPendingProcessing: 0,
        countEnrolled: 0,
        countWaitlisted: 0,
        totalActiveSeats: 0,
        totalAllocatedCapacity: 0,
        facilities: []
      }
    }

    // Aggregate provider totals
    accumulateMetrics(acc[providerKey], row)

    // Check if facility already exists
    const existingFacility = acc[providerKey].facilities.find(f =>
      f.facilityName === row.facilityName
    )

    if (existingFacility === undefined) {
      acc[providerKey].facilities.push({
        providerName: row.providerName,
        facilityName: row.facilityName,
        facilityAddress: row.facilityAddress,
        countPendingProcessing: row.countPendingProcessing,
        countEnrolled: row.countEnrolled,
        countWaitlisted: row.countWaitlisted,
        totalActiveSeats: row.totalActiveSeats,
        totalAllocatedCapacity: row.totalAllocatedCapacity
      })
    } else {
      accumulateMetrics(existingFacility, row)
    }

    return acc
  }, {})

  return Object.values(grouped)
}

/**
 * Calculate summary statistics for the entire dataset
 */
export function calculateSummaryStats (data: GSRPCapacityReportModel[]): {
  totalCapacity: number
  totalActiveSeats: number
  openCapacity: number
  utilizationPercent: number
  totalPending: number
  totalWaitlisted: number
  providerCount: number
  facilityCount: number
} {
  const totalCapacity = data.reduce((sum, row) => sum + row.totalAllocatedCapacity, 0)
  const totalActiveSeats = data.reduce((sum, row) => sum + row.totalActiveSeats, 0)
  const totalPending = data.reduce((sum, row) => sum + row.countPendingProcessing, 0)
  const totalWaitlisted = data.reduce((sum, row) => sum + row.countWaitlisted, 0)

  const uniqueProviders = new Set(data.map(row => row.providerName))
  const uniqueFacilities = new Set(data.map(row => `${row.providerName}|${row.facilityName}`))

  const openCapacity = Math.max(0, totalCapacity - totalActiveSeats)
  const utilizationPercent = getUtilizationPercent(totalActiveSeats, totalCapacity)

  return {
    totalCapacity,
    totalActiveSeats,
    openCapacity,
    utilizationPercent,
    totalPending,
    totalWaitlisted,
    providerCount: uniqueProviders.size,
    facilityCount: uniqueFacilities.size
  }
}

/**
 * Get short year format for badge (e.g., "26/27" from "School Year 2026/2027")
 */
export function getShortYear (timespanName: string): string {
  const match = timespanName.match(/(\d{4})\/(\d{4})/)
  if (match != null) {
    return `${match[1].slice(-2)}/${match[2].slice(-2)}`
  }
  return timespanName.slice(0, 5)
}
